import { useEffect, useRef, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ZoomIn, ZoomOut, Camera, ChevronLeft, ChevronRight, 
  Maximize, RotateCcw, Hand
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Touch Gesture Controls for Skybot
 * 
 * Supported Gestures:
 * - Pinch: Zoom in/out
 * - Double-tap: Capture photo
 * - Swipe Left/Right: Switch filters
 * - Swipe Up: Open fullscreen
 * - Swipe Down: Close fullscreen / Reset filters
 * - Two-finger tap: Switch camera
 * - Long press: Start/Stop recording
 * - Triple tap: AI Analyze
 */

// Audio feedback for gestures
class GestureAudio {
  constructor() {
    this.audioContext = null;
  }

  init() {
    if (this.audioContext) return;
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.warn('Gesture audio not available');
    }
  }

  beep(frequency = 440, duration = 0.1, volume = 0.2) {
    if (!this.audioContext) this.init();
    if (!this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);
    } catch (e) {}
  }

  zoom() {
    this.beep(800, 0.05, 0.15);
  }

  capture() {
    this.beep(1500, 0.08, 0.2);
    setTimeout(() => this.beep(1000, 0.05, 0.15), 80);
  }

  swipe() {
    this.beep(600, 0.06, 0.15);
  }

  longPress() {
    this.beep(400, 0.15, 0.2);
  }
}

const gestureAudio = new GestureAudio();

// Gesture indicator component
const GestureIndicator = ({ gesture, position }) => {
  const { theme } = useTheme();
  
  const icons = {
    'zoom-in': <ZoomIn className="w-8 h-8" />,
    'zoom-out': <ZoomOut className="w-8 h-8" />,
    'capture': <Camera className="w-8 h-8" />,
    'swipe-left': <ChevronLeft className="w-8 h-8" />,
    'swipe-right': <ChevronRight className="w-8 h-8" />,
    'swipe-up': <Maximize className="w-8 h-8" />,
    'swipe-down': <RotateCcw className="w-8 h-8" />,
    'long-press': <Hand className="w-8 h-8" />
  };

  const labels = {
    'zoom-in': 'Zoom In',
    'zoom-out': 'Zoom Out',
    'capture': 'Captured!',
    'swipe-left': 'Previous Filter',
    'swipe-right': 'Next Filter',
    'swipe-up': 'Fullscreen',
    'swipe-down': 'Reset',
    'long-press': 'Recording'
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5 }}
      className="fixed z-50 flex flex-col items-center gap-2 pointer-events-none"
      style={{
        left: position?.x || '50%',
        top: position?.y || '50%',
        transform: 'translate(-50%, -50%)'
      }}
    >
      <div 
        className="p-4 rounded-full backdrop-blur-xl"
        style={{
          background: `${theme?.colors?.primary || '#00f2ea'}30`,
          border: `2px solid ${theme?.colors?.primary || '#00f2ea'}`,
          boxShadow: `0 0 30px ${theme?.colors?.primary || '#00f2ea'}50`
        }}
      >
        <span style={{ color: theme?.colors?.primary || '#00f2ea' }}>
          {icons[gesture] || icons['capture']}
        </span>
      </div>
      <span 
        className="text-sm font-bold px-3 py-1 rounded-full backdrop-blur-xl"
        style={{
          background: 'rgba(0,0,0,0.7)',
          color: theme?.colors?.primary || '#00f2ea'
        }}
      >
        {labels[gesture] || gesture}
      </span>
    </motion.div>
  );
};

// Zoom level indicator
const ZoomIndicator = ({ level, visible }) => {
  const { theme } = useTheme();
  
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none"
        >
          <div 
            className="px-6 py-3 rounded-2xl backdrop-blur-xl"
            style={{
              background: 'rgba(0,0,0,0.8)',
              border: `2px solid ${theme?.colors?.primary || '#00f2ea'}40`,
              boxShadow: `0 0 40px ${theme?.colors?.primary || '#00f2ea'}30`
            }}
          >
            <span 
              className="font-unbounded font-bold text-3xl"
              style={{ color: theme?.colors?.primary || '#00f2ea' }}
            >
              {level.toFixed(1)}x
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main hook for gesture handling
export const useGestureControls = ({
  containerRef,
  enabled = true,
  onZoom,
  zoomLevel = 1,
  maxZoom = 200,
  minZoom = 0.5,
  onCapture,
  onStartRecording,
  onStopRecording,
  isRecording,
  onNextFilter,
  onPrevFilter,
  onFullscreen,
  onReset,
  onSwitchCamera,
  onAnalyze,
  audioEnabled = true
}) => {
  const [activeGesture, setActiveGesture] = useState(null);
  const [gesturePosition, setGesturePosition] = useState(null);
  const [showZoomIndicator, setShowZoomIndicator] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(zoomLevel);
  
  // Touch tracking refs
  const touchStartRef = useRef({ x: 0, y: 0, time: 0, touches: 0 });
  const lastTapRef = useRef({ time: 0, count: 0 });
  const pinchStartDistanceRef = useRef(0);
  const longPressTimerRef = useRef(null);
  const isGestureActiveRef = useRef(false);

  // Calculate distance between two touch points
  const getTouchDistance = useCallback((touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  // Get center point of touches
  const getTouchCenter = useCallback((touches) => {
    if (touches.length === 0) return { x: 0, y: 0 };
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  }, []);

  // Show gesture feedback
  const showGestureFeedback = useCallback((gesture, position = null) => {
    setActiveGesture(gesture);
    setGesturePosition(position || { x: '50%', y: '50%' });
    setTimeout(() => {
      setActiveGesture(null);
    }, 800);
  }, []);

  // Handle touch start
  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;
    
    const touches = e.touches;
    const now = Date.now();
    const center = getTouchCenter(touches);
    
    touchStartRef.current = {
      x: center.x,
      y: center.y,
      time: now,
      touches: touches.length
    };

    // Initialize audio on first touch
    if (audioEnabled) gestureAudio.init();

    // Check for double/triple tap
    if (touches.length === 1) {
      const timeSinceLastTap = now - lastTapRef.current.time;
      
      if (timeSinceLastTap < 300) {
        lastTapRef.current.count++;
        
        // Triple tap - Analyze
        if (lastTapRef.current.count >= 3) {
          lastTapRef.current.count = 0;
          onAnalyze?.();
          if (audioEnabled) gestureAudio.capture();
          showGestureFeedback('capture', { x: `${center.x}px`, y: `${center.y}px` });
          toast.success('🤖 AI Analysis triggered');
          return;
        }
      } else {
        lastTapRef.current.count = 1;
      }
      lastTapRef.current.time = now;

      // Long press detection for recording
      longPressTimerRef.current = setTimeout(() => {
        if (isRecording) {
          onStopRecording?.();
          if (audioEnabled) gestureAudio.longPress();
          showGestureFeedback('long-press', { x: `${center.x}px`, y: `${center.y}px` });
          toast.success('⏹️ Recording stopped');
        } else {
          onStartRecording?.();
          if (audioEnabled) gestureAudio.longPress();
          showGestureFeedback('long-press', { x: `${center.x}px`, y: `${center.y}px` });
          toast.success('🔴 Recording started');
        }
        isGestureActiveRef.current = true;
      }, 600);
    }

    // Two-finger tap - Switch camera
    if (touches.length === 2) {
      clearTimeout(longPressTimerRef.current);
      pinchStartDistanceRef.current = getTouchDistance(touches);
      
      // Check for two-finger tap (not pinch)
      setTimeout(() => {
        if (!isGestureActiveRef.current && touches.length === 2) {
          const currentDist = pinchStartDistanceRef.current;
          if (Math.abs(currentDist - getTouchDistance(e.touches)) < 20) {
            onSwitchCamera?.();
            if (audioEnabled) gestureAudio.capture();
            toast.success('📷 Camera switched');
          }
        }
      }, 200);
    }
  }, [enabled, audioEnabled, getTouchCenter, getTouchDistance, showGestureFeedback, 
      isRecording, onStartRecording, onStopRecording, onSwitchCamera, onAnalyze]);

  // Handle touch move
  const handleTouchMove = useCallback((e) => {
    if (!enabled) return;
    
    const touches = e.touches;
    
    // Pinch to zoom
    if (touches.length === 2) {
      clearTimeout(longPressTimerRef.current);
      isGestureActiveRef.current = true;
      
      const currentDistance = getTouchDistance(touches);
      const startDistance = pinchStartDistanceRef.current;
      
      if (startDistance > 0) {
        const scale = currentDistance / startDistance;
        const newZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel * scale));
        
        if (Math.abs(newZoom - currentZoom) > 0.1) {
          setCurrentZoom(newZoom);
          setShowZoomIndicator(true);
          onZoom?.(newZoom);
          
          if (audioEnabled && Math.abs(newZoom - currentZoom) > 0.5) {
            gestureAudio.zoom();
          }
        }
      }
      
      pinchStartDistanceRef.current = currentDistance;
    }
  }, [enabled, audioEnabled, getTouchDistance, zoomLevel, currentZoom, minZoom, maxZoom, onZoom]);

  // Handle touch end
  const handleTouchEnd = useCallback((e) => {
    if (!enabled) return;
    
    clearTimeout(longPressTimerRef.current);
    setShowZoomIndicator(false);
    
    const now = Date.now();
    const touchDuration = now - touchStartRef.current.time;
    const touches = e.changedTouches;
    
    if (touches.length === 0) return;
    
    const endX = touches[0].clientX;
    const endY = touches[0].clientY;
    const deltaX = endX - touchStartRef.current.x;
    const deltaY = endY - touchStartRef.current.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Skip if pinch gesture was active
    if (isGestureActiveRef.current) {
      isGestureActiveRef.current = false;
      return;
    }

    // Double tap - Capture
    if (touchStartRef.current.touches === 1 && touchDuration < 300 && distance < 30) {
      if (lastTapRef.current.count === 2) {
        lastTapRef.current.count = 0;
        onCapture?.();
        if (audioEnabled) gestureAudio.capture();
        showGestureFeedback('capture', { x: `${endX}px`, y: `${endY}px` });
        toast.success('📸 Photo captured!');
        return;
      }
    }

    // Swipe detection (minimum 80px, max 500ms)
    if (touchDuration < 500 && distance > 80) {
      const angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
      
      // Swipe Right (filter next)
      if (angle > -45 && angle < 45) {
        onNextFilter?.();
        if (audioEnabled) gestureAudio.swipe();
        showGestureFeedback('swipe-right');
        return;
      }
      
      // Swipe Left (filter prev)
      if (angle > 135 || angle < -135) {
        onPrevFilter?.();
        if (audioEnabled) gestureAudio.swipe();
        showGestureFeedback('swipe-left');
        return;
      }
      
      // Swipe Up (fullscreen)
      if (angle > -135 && angle < -45) {
        onFullscreen?.();
        if (audioEnabled) gestureAudio.swipe();
        showGestureFeedback('swipe-up');
        toast.success('🖥️ Fullscreen mode');
        return;
      }
      
      // Swipe Down (reset)
      if (angle > 45 && angle < 135) {
        onReset?.();
        if (audioEnabled) gestureAudio.swipe();
        showGestureFeedback('swipe-down');
        toast.success('🔄 Filters reset');
        return;
      }
    }
  }, [enabled, audioEnabled, showGestureFeedback, onCapture, onNextFilter, 
      onPrevFilter, onFullscreen, onReset]);

  // Attach event listeners
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !enabled) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      clearTimeout(longPressTimerRef.current);
    };
  }, [containerRef, enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    activeGesture,
    gesturePosition,
    showZoomIndicator,
    currentZoom: showZoomIndicator ? currentZoom : zoomLevel
  };
};

// Gesture overlay component
const GestureControls = ({
  containerRef,
  enabled = true,
  onZoom,
  zoomLevel = 1,
  maxZoom = 200,
  minZoom = 0.5,
  onCapture,
  onStartRecording,
  onStopRecording,
  isRecording,
  onNextFilter,
  onPrevFilter,
  onFullscreen,
  onReset,
  onSwitchCamera,
  onAnalyze,
  audioEnabled = true
}) => {
  const {
    activeGesture,
    gesturePosition,
    showZoomIndicator,
    currentZoom
  } = useGestureControls({
    containerRef,
    enabled,
    onZoom,
    zoomLevel,
    maxZoom,
    minZoom,
    onCapture,
    onStartRecording,
    onStopRecording,
    isRecording,
    onNextFilter,
    onPrevFilter,
    onFullscreen,
    onReset,
    onSwitchCamera,
    onAnalyze,
    audioEnabled
  });

  return (
    <>
      {/* Gesture Indicator */}
      <AnimatePresence>
        {activeGesture && (
          <GestureIndicator 
            gesture={activeGesture} 
            position={gesturePosition}
          />
        )}
      </AnimatePresence>

      {/* Zoom Level Indicator */}
      <ZoomIndicator level={currentZoom} visible={showZoomIndicator} />
    </>
  );
};

export default GestureControls;
